import { ReactNode } from 'react';

import classnames from 'classnames';

import { checkItemMatch } from 'components/Filters/checkItemMatch';
import { useFiltersContext } from 'components/Filters/FiltersContext';
import { FiltersState, ToggleSelectFilterConfig, ToggleState } from 'components/Filters/types';
import Repeat from '@lotlinx-aws/sparky-component-library/Sparky/Repeat';
import Skeleton from '@lotlinx-aws/sparky-component-library/Sparky/Skeleton';
import Spacer from '@lotlinx-aws/sparky-component-library/Sparky/Spacer';
import SwitchField from '@lotlinx-aws/sparky-component-library/Sparky/SwitchField';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import Tooltip from '@lotlinx-aws/sparky-component-library/Sparky/Tooltip';

import styles from './ToggleOptions.module.scss';

export interface ToggleOptionsProps {
  config: ToggleSelectFilterConfig<unknown>;
  toggleOptionsLabel?: ReactNode;
  allowDisabled?: boolean;
  disabled?: boolean;
  oppositeChecked?: boolean;
  showToggleOptionsItemCount?: boolean;
}

/**
 * ToggleOptions
 */
export default function ToggleOptions({
  config,
  toggleOptionsLabel,
  allowDisabled = true,
  disabled = false,
  oppositeChecked = false,
  showToggleOptionsItemCount = true,
}: ToggleOptionsProps) {
  const {
    data,
    getFilterState,
    setFilterState,
    clearFilter,
    isLoading,
    filtersState,
    filterConfigs,
    itemCountLabel,
    singularItemCountLabel,
    showItemCount,
  } = useFiltersContext();

  const state = getFilterState(config.id) as ToggleState | undefined;

  const otherFiltersState: FiltersState = { ...filtersState };
  delete otherFiltersState[config.id];
  const filteredData =
    data?.slice().filter((item) => checkItemMatch(item, filterConfigs, otherFiltersState)) ?? [];

  const handleChange = (optionKey: string | number, checked: boolean) => {
    let opts: (string | number)[] = [];
    if (state && state.options?.length) {
      opts = [...state.options];
    }

    if (checked) {
      if (oppositeChecked) {
        opts.push(optionKey);
      } else {
        opts = opts.filter((i) => i !== optionKey);
      }
    } else {
      if (oppositeChecked) {
        opts = opts.filter((i) => i !== optionKey);
      } else {
        opts.push(optionKey);
      }
    }

    if (!opts.length) {
      clearFilter(config.id);
    } else {
      setFilterState(config.id, { options: opts });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.toggleOptions}>
        <Repeat times={1}>
          <div className={styles.option} style={{ height: '1.25em' }}>
            <Skeleton width='10em' height='1em' />
            {showItemCount && showToggleOptionsItemCount && <Skeleton width='2em' height='1em' />}
          </div>
        </Repeat>
      </div>
    );
  }

  let options: ReactNode[] = Object.entries(config.options).map(([key, opt]) => {
    const matchCount = filteredData.filter((item) => opt.matcher(item)).length;

    const checked = oppositeChecked
      ? !state?.options?.find((i) => i === key)
      : !!state?.options?.find((i) => i === key);

    return (
      <Tooltip
        key={key}
        content={config.toolTip}
        disabled={!((!matchCount && allowDisabled) || disabled) || !config.toolTip}
        side={config.side}
        className={styles.tooltip}
      >
        <div className={styles.toggleOption}>
          <SwitchField
            id={opt.label}
            disabled={(!matchCount && allowDisabled) || disabled}
            checked={checked}
            onCheckedChange={() => handleChange(key, checked)}
            label={toggleOptionsLabel ? toggleOptionsLabel : opt.label}
          />
          <Spacer axis='horizontal' style={{ margin: '0 auto' }} />
          {showItemCount && showToggleOptionsItemCount && (
            <Text
              size='12'
              color={disabled ? 'disabled' : 'secondary'}
              className={classnames([styles.itemCount, !matchCount && styles.muted])}
            >
              {matchCount}{' '}
              {matchCount === 1 ? (singularItemCountLabel ?? itemCountLabel) : itemCountLabel}
            </Text>
          )}
        </div>
      </Tooltip>
    );
  });

  if (!options.filter((i) => !!i).length) {
    return (
      <div className={styles.selectOptions}>
        <Text size='14'>No options available</Text>
      </div>
    );
  }

  return <div className={styles.selectOptions}>{options}</div>;
}
